import React, { useEffect, useRef, useState } from 'react';

import * as S from './styles';
import {
  DocumentProps,
  HandleChangePdfRenderProps,
  PdfPageStateProps,
  PDFViewerRef,
} from './utils/interfaces';
import { EmptyDocs } from './components/EmptyDocs';
import { Header } from './components/Header';
import { Sidebar } from './components/Sidebar';
import { ArrowBackIos, ArrowForwardIos } from '@material-ui/icons';
import { DocumentView } from './components/DocumentView';

interface ListDocumentsViewerProps {
  onClose: () => void;
  listPdfs: DocumentProps[];
  hideHeader?: boolean;
  hideSidebar?: boolean;
  handleClickButtonDelete?: ({ documentId }: { documentId: number }) => void;
  isLoading?: boolean;
  handleDownloadDocuments?: ({
    documentIds,
  }: {
    documentIds: number[];
  }) => void;
  isLoadingDownload?: boolean;
}

export const ListDocumentsViewer = ({
  onClose,
  listPdfs,
  hideHeader = false,
  hideSidebar = false,
  handleClickButtonDelete,
  isLoading = false,
  handleDownloadDocuments,
  isLoadingDownload,
}: ListDocumentsViewerProps) => {
  const initialState: PdfPageStateProps = {
    downloadClicked: false,
    scale: 1.5,
    totalDocuments: 1,
    currentDocumentIndex: 0,
    numPages: 0,
  };

  const pdfViewerRef = useRef<PDFViewerRef>(null);

  const [listDocumentsViewerState, setListDocumentsViewerState] = useState<
    PdfPageStateProps
  >(initialState);

  const [pdfRender, setPdfRender] = useState<DocumentProps>(listPdfs?.[0]);
  const [hideSidebarState, setHideSidebarState] = useState<boolean>(
    hideSidebar
  );

  const currentIndex = listPdfs.findIndex(el => el.id === pdfRender?.id);
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === listPdfs.length - 1;

  const handleChangePdfRender = ({
    idPdf,
    nextDocument,
    previousDocument,
  }: HandleChangePdfRenderProps) => {
    if (!listPdfs || listPdfs.length === 0) return;

    if (idPdf) {
      const pdfFound = listPdfs.find(el => el.id === idPdf);
      setPdfRender(pdfFound ?? listPdfs[0]);
      return;
    }

    const currentIndex = listPdfs.findIndex(el => el.id === pdfRender?.id);

    if (nextDocument) {
      const nextIndex =
        currentIndex + 1 < listPdfs.length ? currentIndex + 1 : currentIndex;
      setPdfRender(listPdfs[nextIndex]);
      return;
    }

    if (previousDocument) {
      const prevIndex = currentIndex - 1 >= 0 ? currentIndex - 1 : currentIndex;
      setPdfRender(listPdfs[prevIndex]);
      return;
    }
  };

  const handleShowSidebar = () => {
    setHideSidebarState(!hideSidebarState);
  };

  const handleCloseModal = () => {
    onClose();
    setListDocumentsViewerState(initialState);
  };

  useEffect(() => {
    setListDocumentsViewerState(prev => ({
      ...prev,
      totalDocuments: listPdfs.length,
      currentDocumentIndex: currentIndex + 1,
    }));
  }, [currentIndex, listPdfs.length]);

  useEffect(() => {
    if (!listPdfs?.length) return;
    if (pdfRender?.id !== listPdfs[0]?.id) {
      setPdfRender(listPdfs[0]);
    }
  }, [listPdfs]);

  useEffect(() => {
    setHideSidebarState(hideSidebar);
  }, [hideSidebar]);

  useEffect(() => {
    if (!!listDocumentsViewerState?.downloadClicked && hideSidebarState) {
      setHideSidebarState(false);
    }
  }, [listDocumentsViewerState?.downloadClicked]);

  if (!listPdfs?.length && !isLoading) {
    return (
      <S.Container>
        <EmptyDocs onClose={handleCloseModal} />
      </S.Container>
    );
  }

  return (
    <S.Container>
      {!hideHeader && (
        <Header
          onClose={handleCloseModal}
          pdfViewerRef={pdfViewerRef}
          title={pdfRender?.name}
          handleClickButtonDelete={handleClickButtonDelete}
          isLoading={isLoading && !pdfRender?.documentUrl}
          pdfRender={pdfRender}
          listDocumentsViewerState={listDocumentsViewerState}
          setListDocumentsViewerState={setListDocumentsViewerState}
          handleShowSidebar={handleShowSidebar}
          hideSidebar={hideSidebarState}
        />
      )}

      <S.ContentWrapper>
        {!hideSidebarState && (
          <S.SidebarWrapper>
            <Sidebar
              documents={listPdfs}
              handlePdfRender={handleChangePdfRender}
              pdfRenderId={pdfRender?.id}
              handleDownloadDocuments={handleDownloadDocuments}
              isLoadingDownload={isLoadingDownload}
              listDocumentsViewerState={listDocumentsViewerState}
              setListDocumentsViewerState={setListDocumentsViewerState}
            />
          </S.SidebarWrapper>
        )}

        <S.ContainerDocumentMain data-hide-sidebar={hideSidebarState}>
          <S.ArrowContainer
            onClick={() => {
              if (isFirst) return;
              handleChangePdfRender({ previousDocument: true });
            }}
            data-disabled={isFirst}
            direction="left"
          >
            <ArrowBackIos fontSize="inherit" />
          </S.ArrowContainer>

          <DocumentView
            ref={pdfViewerRef}
            fileUrl={pdfRender?.documentUrl}
            isLoading={isLoading && !pdfRender?.documentUrl}
            showSinglePage={false}
            setListDocumentsViewerState={setListDocumentsViewerState}
            listDocumentsViewerState={listDocumentsViewerState}
          />

          <S.ArrowContainer
            onClick={() => {
              if (isLast) return;

              return handleChangePdfRender({ nextDocument: true });
            }}
            data-disabled={isLast}
            direction="right"
          >
            <ArrowForwardIos fontSize="inherit" />
          </S.ArrowContainer>
        </S.ContainerDocumentMain>
      </S.ContentWrapper>
    </S.Container>
  );
};
